import { useCallback, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
    MRT_RowSelectionState,
    MRT_ColumnFiltersState,
    MRT_SortingState,
    MRT_ColumnDef,
} from 'material-react-table'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import { useAppSelector } from '../../redux/hook'
import theme from '../../theme/theme'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import { UserPermissions } from '../../constants/constants'
import {
    IsOpenAddOrEditProgram,
    Program,
    ProgramsListAPIResponse,
} from './interfaces'
import { deleteProgram, fetchPrograms } from '../../services/programServices'
import ProgramInstance from './ProgramInstance'
import { formatDateTimeWithUserPreferences } from '../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import { useRoutePermission } from '../../hooks/useRoutePermission'
import { usePermissions } from '../../hooks/usePermissions'

const fetchSize = 10
const actionColumnSize = 40

const actionCellSx = {
    alignItems: 'center',
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    display: 'flex',
    fontSize: '14px',
    justifyContent: 'center',
    padding: '0px',
    textAlign: 'center',
}

const actionIconButtonSx = {
    backgroundColor: 'transparent',
    height: 28,
    padding: '2px',
    width: 28,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '0px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                textAlign: 'center',
            },
        },
    },
}

const Programs: React.FC = () => {
    const { t } = useTranslation()
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const org = useAppSelector((state) => state.org.org)
    //checking the permissions, if user doesn't have permission. redirecting to home
    useRoutePermission([
        UserPermissions.ORG_ADMIN,
        UserPermissions.SYSTEM_READER,
    ])
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(() => {
        return hasPermission(UserPermissions.ORG_ADMIN)
    }, [hasPermission])

    const queryClient = useQueryClient()

    const [openAddOrEditProgram, setOpenAddOrEditProgram] =
        useState<IsOpenAddOrEditProgram>({
            id: null,
            open: false,
        })
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )
    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])

    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [programsToDelete, setProgramsToDelete] = useState<string[]>([])

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<ProgramsListAPIResponse>({
            queryKey: [
                'programs-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
            ],
            queryFn: async ({ pageParam }) => {
                const response = await fetchPrograms(
                    org!.id || '',
                    {
                        pageNumber: (pageParam as number) + 1,
                        pageSize: fetchSize,
                        searchText: globalFilter?.trim() || '',
                        filterBy: '',
                        sortOrder: sorting[0]?.desc ? 'DESC' : 'ASC',
                        sortBy: sorting[0]?.id
                            ?.replace(/\s+/g, '')
                            .toUpperCase(),
                    },
                    /**
                     * Try to remove ! at later point of time.
                     */
                )
                return response
            },
            initialPageParam: 0,
            getNextPageParam: (_lastGroup, groups) => groups.length,
            refetchOnWindowFocus: false,
        })

    const flatData = useMemo(
        () => data?.pages?.flatMap((page) => page?.programs ?? []) ?? [],
        [data],
    )

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.meta?.totalRowCount ?? 0,
        [data],
    )

    const openAddProgram = () => {
        setOpenAddOrEditProgram({
            id: null,
            open: true,
        })
    }

    const openEditProgram = useCallback((rowData: Program) => {
        setOpenAddOrEditProgram({
            id: rowData?.id || null,
            open: true,
        })
    }, [])

    const onCloseAddorEditProgramPopup = (refresh: boolean) => {
        if (refresh) {
            //refreshing the list
            queryClient.removeQueries({ queryKey: ['programs-table-data'] })
            queryClient.invalidateQueries({ queryKey: ['programs-table-data'] })
        }
        setRowSelection({})
        setOpenAddOrEditProgram({
            id: null,
            open: false,
        })
    }

    const deleteOrgHandler = () => {
        const keys = Object.keys(rowSelection)
        const programsToDelete: string[] = []
        for (const keyStr of keys) {
            const key = Number(keyStr)
            const rowData: Program = flatData[key]

            programsToDelete.push(rowData.id)
        }
        setProgramsToDelete(programsToDelete)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = async () => {
        await deleteProgram(programsToDelete[0] || '')
        setRowSelection({})
        setOpenDeleteDialog(false)
        //refreshing the list
        queryClient.removeQueries({ queryKey: ['programs-table-data'] })
        queryClient.invalidateQueries({ queryKey: ['programs-table-data'] })
    }

    const disableEditButtons = useMemo(
        () => Object.keys(rowSelection).length !== 1,
        [rowSelection],
    )

    const columns: MRT_ColumnDef<Program>[] = useMemo(
        () => [
            {
                header: '',
                accessorKey: 'editid',
                id: 'editid',
                Cell: ({ row }) => (
                    <IconButton
                        id={`row-${row.id}-edit-button`}
                        onClick={(event) => {
                            event.stopPropagation()
                            openEditProgram(row.original)
                        }}
                        aria-controls="simple-menu-preview"
                        aria-haspopup="true"
                        aria-label={
                            hasWriteAccess ? t('editProgram') : t('viewProgram')
                        }
                        sx={actionIconButtonSx}
                        title={
                            hasWriteAccess ? t('editProgram') : t('viewProgram')
                        }
                    >
                        {hasWriteAccess ? (
                            <img
                                src="/assets/images/icons/edit.svg"
                                alt={t('editProgram')}
                            />
                        ) : (
                            <img
                                src="/assets/images/icons/preview.svg"
                                alt={t('viewProgram')}
                            />
                        )}
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                },
            },
            {
                accessorKey: 'name',
                header: t('name'),
                muiTableBodyCellProps: { role: 'gridcell' },
                maxWidth: 350,
            },
            {
                accessorKey: 'ownerName',
                header: t('buyer'),
                muiTableBodyCellProps: { role: 'gridcell' },
                maxWidth: 350,
            },
            {
                accessorKey: 'programTypeName',
                header: t('programType'),
                enableFilterMatchHighlighting: false,
                muiTableBodyCellProps: { role: 'gridcell' },
                maxWidth: 350,
            },
            {
                id: 'SUBSCRIBERS',
                accessorFn: (row) => {
                    if (row?.subscribers) {
                        return row.subscribers.map((i) => i.name)?.join(', ')
                    } else {
                        return ''
                    }
                },
                header: t('shipper'),
                enableFilterMatchHighlighting: false,
                muiTableBodyCellProps: { role: 'gridcell' },
                maxWidth: 350,
            },
            {
                accessorFn: (row) => {
                    if (row.createdAt) {
                        return formatDateTimeWithUserPreferences(
                            row.createdAt,
                            userLocalization,
                        )
                    } else {
                        return ''
                    }
                },
                header: t('createdAt'),
                enableFilterMatchHighlighting: false,
                muiTableBodyCellProps: { role: 'gridcell' },
                maxWidth: 350,
            },
        ],
        [hasWriteAccess, openEditProgram, userLocalization, t],
    )

    if (openAddOrEditProgram.open) {
        return (
            <ProgramInstance
                instanceId={openAddOrEditProgram.id}
                onClose={(refresh: boolean) =>
                    onCloseAddorEditProgramPopup(refresh)
                }
                readOnly={!hasWriteAccess}
            />
        )
    }

    return (
        <Grid
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Grid>
                    <Typography variant="h4" component="h2">
                        {t('programs')}
                    </Typography>
                </Grid>
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            role="search"
                            id="program-list-search"
                            inputProps={{
                                'aria-label': t('searchPrograms'),
                            }}
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            id="program-list-search-icon"
                                            aria-hidden="true"
                                            tabIndex={-1}
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {hasWriteAccess ? (
                        <>
                            <Box
                                sx={{
                                    cursor: disableEditButtons
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={deleteOrgHandler}
                                    disabled={disableEditButtons}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableEditButtons
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableEditButtons ? 0.5 : 1,
                                    }}
                                    id="delete-program-button"
                                    aria-label={t('deleteProgram')}
                                    title={t('deleteProgram')}
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteProgram')}
                                    />
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    position: 'relative',
                                }}
                            >
                                <Button
                                    onClick={openAddProgram}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '15px',
                                        minHeight: '25px',
                                        maxHeight: '30px',
                                        marginRight: '10px',
                                    }}
                                    id="add-program-button"
                                    aria-label={t('addProgram')}
                                    title={t('addProgram')}
                                >
                                    <img
                                        src={`/assets/images/icons/add-icon.svg`}
                                        alt={t('addProgram')}
                                    />
                                </Button>
                            </Box>
                        </>
                    ) : null}
                </Grid>
            </Grid>
            <MRTTable<Program, ProgramsListAPIResponse>
                key={JSON.stringify(userLocalization) || 'default'}
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                maxHeight="calc(100vh - 348px)"
                layoutMode="grid"
            />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('deleteProgramConfirmation')}
            />
        </Grid>
    )
}

export default Programs
